// 设备信息类型定义
export interface DeviceInfo {
  // 基础设备信息
  userAgent: string;
  platform: string;
  os: string;
  language: string;
  timezone: string;
  screenResolution: string;
  colorDepth: number;

  // 浏览器信息
  browser: {
    name: string;
    version: string;
    engine: string;
  };

  // 设备类型
  deviceType: "mobile" | "tablet" | "desktop";

  // 网络信息
  connection: {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
  };

  // 位置信息（如果可用）
  location?: {
    latitude?: number;
    longitude?: number;
    accuracy?: number;
  };

  // 会话信息
  session: {
    sessionId: string;
    timestamp: number;
  };

  // 设备指纹
  fingerprint?: string;
}

// 获取浏览器信息
function getBrowserInfo(): DeviceInfo["browser"] {
  const userAgent = navigator.userAgent;

  // Chrome
  if (userAgent.includes("Chrome") && !userAgent.includes("Edg")) {
    const version = userAgent.match(/Chrome\/(\d+\.\d+)/)?.[1] || "unknown";
    return { name: "Chrome", version, engine: "Blink" };
  }

  // Firefox
  if (userAgent.includes("Firefox")) {
    const version = userAgent.match(/Firefox\/(\d+\.\d+)/)?.[1] || "unknown";
    return { name: "Firefox", version, engine: "Gecko" };
  }

  // Safari
  if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) {
    const version = userAgent.match(/Version\/(\d+\.\d+)/)?.[1] || "unknown";
    return { name: "Safari", version, engine: "WebKit" };
  }

  // Edge
  if (userAgent.includes("Edg")) {
    const version = userAgent.match(/Edg\/(\d+\.\d+)/)?.[1] || "unknown";
    return { name: "Edge", version, engine: "Blink" };
  }

  return { name: "Unknown", version: "unknown", engine: "unknown" };
}

// 获取设备类型
function getDeviceType(): DeviceInfo["deviceType"] {
  const userAgent = navigator.userAgent;

  if (
    /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)
  ) {
    if (/iPad/i.test(userAgent)) {
      return "tablet";
    }
    return "mobile";
  }

  return "desktop";
}

// 获取网络连接信息
function getConnectionInfo(): DeviceInfo["connection"] {
  const connection =
    (navigator as any).connection ||
    (navigator as any).mozConnection ||
    (navigator as any).webkitConnection;

  if (connection) {
    return {
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
    };
  }

  return {};
}

// 获取位置信息（需要用户授权）
function getLocationInfo(): Promise<DeviceInfo["location"]> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(undefined);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      () => {
        resolve(undefined);
      },
      {
        timeout: 5000,
        enableHighAccuracy: false,
        maximumAge: 300000, // 5 minutes
      }
    );
  });
}

// 生成设备会话 ID - 在设备上保持一致
function generateSessionId(): string {
  // 首先尝试从 localStorage 获取现有的会话ID（持久化存储）
  const existingSessionId = localStorage.getItem("device_session_id");
  if (existingSessionId) {
    return existingSessionId;
  }

  // 如果没有现有会话ID，生成一个新的并存储
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substring(2, 15);
  const newSessionId = `device-${timestamp}-${randomStr}`;

  // 存储到 localStorage，这样在同一设备上会持久保持一致
  localStorage.setItem("device_session_id", newSessionId);

  console.log("Generated new device session ID:", newSessionId);
  return newSessionId;
}

// 获取完整的设备信息
export async function getDeviceInfo(): Promise<DeviceInfo> {
  const deviceInfo: DeviceInfo = {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screenResolution: `${screen.width}x${screen.height}`,
    colorDepth: screen.colorDepth,
    browser: getBrowserInfo(),
    deviceType: getDeviceType(),
    connection: getConnectionInfo(),
    os: getDeviceOs(),
    session: {
      sessionId: generateSessionId(),
      timestamp: Date.now(),
    },
  };

  // 尝试获取位置信息（非阻塞）
  try {
    const location = await getLocationInfo();
    if (location) {
      deviceInfo.location = location;
    }
  } catch (error) {
    console.debug("Location access denied or failed:", error);
  }

  return deviceInfo;
}

// 获取设备信息的简化版本（同步）
export function getDeviceInfoSync(): Omit<DeviceInfo, "location"> {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screenResolution: `${screen.width}x${screen.height}`,
    colorDepth: screen.colorDepth,
    browser: getBrowserInfo(),
    deviceType: getDeviceType(),
    connection: getConnectionInfo(),
    os: getDeviceOs(),
    session: {
      sessionId: generateSessionId(),
      timestamp: Date.now(),
    },
  };
}

// 设备信息缓存
let cachedDeviceInfo: DeviceInfo | null = null;

// 获取缓存的设备信息
export function getCachedDeviceInfo(): DeviceInfo | null {
  return cachedDeviceInfo;
}

// 设置设备信息缓存
export function setCachedDeviceInfo(deviceInfo: DeviceInfo): void {
  cachedDeviceInfo = deviceInfo;
}

// 清除设备信息缓存
export function clearCachedDeviceInfo(): void {
  cachedDeviceInfo = null;
}

// 清除设备会话ID（用于重置设备指纹）
export function clearDeviceSessionId(): void {
  localStorage.removeItem("device_session_id");
  console.log("Device session ID cleared");
}

// 初始化设备信息（在应用启动时调用）
export async function initDeviceInfo(): Promise<DeviceInfo> {
  if (cachedDeviceInfo) {
    return cachedDeviceInfo;
  }

  const deviceInfo = await getDeviceInfo();

  // 生成设备指纹
  try {
    const { generateDeviceFingerprint } = await import("./fingerprint");
    deviceInfo.fingerprint = await generateDeviceFingerprint(deviceInfo);
  } catch (error) {
    console.error(
      "Failed to generate fingerprint during device info initialization:",
      error
    );
    // 使用降级指纹
    deviceInfo.fingerprint = `fallback-${deviceInfo.session.sessionId}`;
  }

  setCachedDeviceInfo(deviceInfo);

  console.log("📱 Device info initialized:", {
    deviceType: deviceInfo.deviceType,
    browser: deviceInfo.browser.name,
    platform: deviceInfo.platform,
    sessionId: deviceInfo.session.sessionId,
    fingerprint: deviceInfo.fingerprint?.substring(0, 16) + "...",
  });

  return deviceInfo;
}

export function getDeviceOs(): string {
  const platform = navigator.platform.toLowerCase();
  if (platform.includes("win")) return "Windows";
  if (platform.includes("mac")) return "macOS";
  if (platform.includes("linux")) return "Linux";
  if (/iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase())) return "iOS";
  if (/android/.test(navigator.userAgent.toLowerCase())) return "Android";
  return "Unknown";
}
